import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building, ArrowRightLeft, TrendingUp, DollarSign } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  FRANCHISE / MULTI-BRANCH (Phase 13)
//  - Consolidated P&L across all branches
//  - Per-branch KPIs
//  - Patient transfer between branches
// ═══════════════════════════════════════════════════════════════════

const FranchisePage = ({ API_BASE }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [pl, setPL] = useState(null);
  const [kpis, setKPIs] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferModal, setTransferModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = `${month}-01`;
      const to = new Date(new Date(month + '-01').setMonth(new Date(month + '-01').getMonth() + 1)).toISOString().slice(0, 10);
      const [p, k, b] = await Promise.all([
        axios.get(`${API_BASE}/franchise/consolidated-pl`, { params: { month } }),
        axios.get(`${API_BASE}/franchise/kpis`, { params: { from, to } }),
        axios.get(`${API_BASE}/branches`),
      ]);
      setPL(p.data); setKPIs(k.data); setBranches(b.data || []);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, month]);

  useEffect(() => { load(); }, [load]);

  const transferPatient = async () => {
    if (!transferModal.patientId || !transferModal.toBranchId) return alert('Select both');
    try {
      const r = await axios.post(`${API_BASE}/franchise/transfer-patient`, transferModal);
      alert(`Transferred: ${r.data.patient.name}`);
      setTransferModal(null);
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const monthOptions = () => {
    const out = []; const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      out.push({ value: v, label: d.toLocaleString('en-IN', { month:'short', year:'numeric' }) });
    }
    return out;
  };

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>🏢 Franchise / Multi-Branch</h2>
        <div style={{ display:'flex', gap:8 }}>
          <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...inputStyle, width:180 }}>
            {monthOptions().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button onClick={() => setTransferModal({ patientId:'', toBranchId:'', reason:'', transferredBy:'admin' })}
            style={{ ...btnStyle, background: theme.secondary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
            <ArrowRightLeft size={14}/> Transfer Patient
          </button>
        </div>
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>}

      {pl && (
        <>
          {/* Grand totals */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:16 }}>
            <StatCard label="Revenue" value={`₹${pl.grand.revenue.toLocaleString('en-IN')}`} color={theme.secondary}/>
            <StatCard label="Collected" value={`₹${pl.grand.collected.toLocaleString('en-IN')}`} color={theme.success}/>
            <StatCard label="Expenses" value={`₹${pl.grand.expenses.toLocaleString('en-IN')}`} color={theme.danger}/>
            <StatCard label="Net Profit" value={`₹${pl.grand.netProfit.toLocaleString('en-IN')}`} color={pl.grand.netProfit >= 0 ? theme.success : theme.danger}/>
            <StatCard label="New Patients" value={pl.grand.patients} color={theme.info}/>
            <StatCard label="Treatments" value={pl.grand.treatments} color={theme.warning}/>
          </div>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden', marginBottom:16 }}>
            <div style={{ padding:'12px 16px', fontWeight:600, color: theme.navy, borderBottom:`1px solid ${theme.border}` }}>
              Branch-wise P&L — {pl.month}
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Branch</th><th style={th}>Revenue</th><th style={th}>Collected</th>
                <th style={th}>Pending</th><th style={th}>Expenses</th><th style={th}>Net Profit</th>
                <th style={th}>New Pt.</th><th style={th}>Appts</th><th style={th}>Treatments</th>
              </tr></thead>
              <tbody>
                {pl.rows.map((r, i) => (
                  <tr key={r.branchId || `u${i}`} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>
                      {r.branchName}{r.code ? ` (${r.code})` : ''}
                    </td>
                    <td style={td}>₹{r.revenue.toLocaleString('en-IN')}</td>
                    <td style={{ ...td, color: theme.success }}>₹{r.collected.toLocaleString('en-IN')}</td>
                    <td style={{ ...td, color: theme.warning }}>₹{r.pending.toLocaleString('en-IN')}</td>
                    <td style={{ ...td, color: theme.danger }}>₹{r.expenses.toLocaleString('en-IN')}</td>
                    <td style={{ ...td, fontWeight:700, color: r.netProfit >= 0 ? theme.success : theme.danger }}>₹{r.netProfit.toLocaleString('en-IN')}</td>
                    <td style={td}>{r.newPatients}</td>
                    <td style={td}>{r.appointments}</td>
                    <td style={td}>{r.treatments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {kpis && (
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', fontWeight:600, color: theme.navy, borderBottom:`1px solid ${theme.border}` }}>
            <TrendingUp size={16} style={{ verticalAlign:'middle', marginRight:6 }}/>
            Monthly KPIs per Branch
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Branch</th><th style={th}>Revenue</th><th style={th}>Treatments</th><th style={th}>ARPU (₹/treatment)</th>
            </tr></thead>
            <tbody>
              {(kpis.rows || []).map(r => (
                <tr key={r.branchId} style={{ borderBottom:'1px solid #f1f3f5' }}>
                  <td style={{ ...td, fontWeight:600 }}>{r.branchName}</td>
                  <td style={td}>₹{r.revenue.toLocaleString('en-IN')}</td>
                  <td style={td}>{r.treatments}</td>
                  <td style={td}>₹{r.arpu.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {transferModal && (
        <div style={modalBg} onClick={() => setTransferModal(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: theme.navy, margin:'0 0 12px' }}>Transfer Patient to Another Branch</h3>
            <label style={lbl}>Patient ID</label>
            <input value={transferModal.patientId} onChange={e => setTransferModal({ ...transferModal, patientId: e.target.value })}
              placeholder="Paste Patient _id here" style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={lbl}>Destination Branch</label>
            <select value={transferModal.toBranchId} onChange={e => setTransferModal({ ...transferModal, toBranchId: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}>
              <option value="">Select branch…</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
            <label style={lbl}>Reason</label>
            <input value={transferModal.reason} onChange={e => setTransferModal({ ...transferModal, reason: e.target.value })} style={{ ...inputStyle, marginBottom:12 }}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setTransferModal(null)} style={{ ...btnStyle, flex:1 }}>Cancel</button>
              <button onClick={transferPatient} style={{ ...btnStyle, flex:1, background: theme.primary, color:'white' }}>Transfer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${color}` }}>
    <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:700, color: theme.navy, marginTop:4 }}>{value}</div>
  </div>
);

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const lbl = { fontSize:12, color:'#555', fontWeight:600, marginBottom:4, display:'block' };
const modalBg = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:500, width:'100%' };

export default FranchisePage;
